import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../lib/data";
import { scrollToId } from "../lib/smoothScroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-obsidian/85 backdrop-blur-xl border-b border-border-subtle/80"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-[72px] flex items-center justify-between">
        <button
          onClick={() => go("home")}
          className="flex items-center gap-2 shrink-0"
          data-testid="nav-brand-logo"
        >
          <span className="font-mono text-laser text-lg sm:text-xl font-medium">{"</>"}</span>
          <span className="font-display font-extrabold text-lg sm:text-xl tracking-wide">
            <span className="text-white">DOTNET</span>
            <span className="text-electric">SERVICES</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => go(link.id)}
              data-testid={`nav-${link.id}-link`}
              className="text-sm font-medium text-slate-300 hover:text-laser transition-colors duration-200 relative group"
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-laser transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            onClick={() => go("contact")}
            data-testid="nav-get-quote-button"
            className="rounded-full bg-electric px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_-4px_rgba(0,102,255,0.7)] hover:shadow-[0_0_28px_-2px_rgba(0,102,255,0.9)] hover:bg-electric/90 transition-all duration-200"
          >
            Get a Quote
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-obsidian/98 backdrop-blur-xl border-b border-border-subtle"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-4 gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => go(link.id)}
                  data-testid={`nav-mobile-${link.id}-link`}
                  className="text-left py-3 text-base font-medium text-slate-200 hover:text-laser border-b border-white/5 transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => go("contact")}
                data-testid="nav-mobile-get-quote-button"
                className="mt-4 rounded-full bg-electric px-5 py-3 text-sm font-semibold text-white text-center"
              >
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
